import Axios from 'axios';
import Item from '../models/Item';
import MetaData from '../models/MetaData';
import WeekTable from '../models/WeekTable';
import TrendTable from '../models/TrendTable';
import SearchResult from '../models/SearchResult';
import WpidSearchResult from '../models/WpidSearchResult';
import { getStartDateAndEndDateStr, getStartDateAndEndDateStrForDownload } from './calendarService';
import DDIRComment from '../models/DDIRComment';
import SavedSearch from '../models/SavedSearch';
import { BASE_API_URL, ENV } from '../constants';

console.log("ENV", ENV);
console.log("process.env", process.env);

const BaseUrl = BASE_API_URL;
// BASE_API_URL always ends in /api-proxy, which routes/index.js mounts as a
// generic passthrough proxy to the *external* DDIR API — it's registered
// before BFF's own native routes (/metaData, /featureFlags, ...), so any
// request prefixed with /api-proxy never reaches those native handlers at
// all. Existing calls (metaData, getSavedSearch, etc.) only "work" through
// /api-proxy because the external API happens to expose matching routes.
// /featureFlags has no external equivalent, so it must bypass /api-proxy
// and hit BFF's own root directly.
const BffRootUrl = BaseUrl.replace(/\/api-proxy\/?$/, '');
const UNAUTHORIZED = 401;
var headers = {};

function getHeaders() {
  return { ...headers, 'WM_QOS.CORRELATION_ID': crypto.randomUUID() };
}

function getCookie(name) {
  const match = document.cookie.match(
    new RegExp('(^|;\\s*)' + name + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[2]) : null;
}

function setAuthToken(token, unauthorizedCallBack) {
  let userDetails = {};
  try {
    const rawCookie = getCookie('user');
    userDetails = rawCookie ? JSON.parse(rawCookie) : {};
    console.log('parsed userDetails in setAuthToken:', userDetails);
  } catch (e) {
    console.warn('failed to parse user cookie in setAuthToken:', e);
    userDetails = {};
  }

  if (ENV.toLowerCase() === 'local') {
    headers = {
      userId: 'testuser',
      rlId: 'testuser@samsclub.com',
      domain: 'home-office'
    };
  } else {
    let domainStr = "N/A";
    let principalname = "";
    const homeOfficeUsers = ["homeoffice.wal-mart.com", "wmsc.wal-mart.com"];

    if (userDetails && (userDetails.userprinciplename || userDetails.userprincipalname)) {
      principalname = userDetails.userprinciplename || userDetails.userprincipalname;
      const principalnameLowerCase = principalname.toLowerCase();
      if (homeOfficeUsers.some(user => principalnameLowerCase.indexOf(user) !== -1)) {
        domainStr = "home-office";
      } else if (principalnameLowerCase.indexOf("retaillink") !== -1) {
        domainStr = "retail-link";
      }
    }
    console.log("domainStr in setAuthToken:", domainStr);
    headers = {
      'Authorization': `Bearer ${token}`,
      userId: userDetails?.nameID,
      domain: domainStr
    };

    Axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === UNAUTHORIZED && unauthorizedCallBack) {
          unauthorizedCallBack();
        }
        return Promise.reject(error);
      }
    );
  }
}

async function getMetaData() {
  const url = BaseUrl + '/metaData';
  const data = (await Axios.get(url, { headers: getHeaders() })).data;
  const metaData = new MetaData(data);
  return metaData;
}

// Fetches Akeyless-backed feature flags (e.g. nonVariantEnabled) from DDIR-BFF.
// Resolved at runtime on every dashboard load — unlike a build-time env var,
// toggling the value in Akeyless takes effect without a redeploy.
async function getFeatureFlags() {
  const url = BffRootUrl + '/featureFlags';
  const data = (await Axios.get(url, { headers: getHeaders() })).data;
  return data;
}

async function searchItems(itemNbrs, dcNbr) {
  const url = BaseUrl + '/searchItems';
  const searchResults = (await Axios.post(url, {
    'itemNbrs': itemNbrs,
    'legacySearch': false,
    'dcNbr': dcNbr
  }, { headers: getHeaders() })).data.map(itemData => new SearchResult(itemData));
  return searchResults ?? [];
}

async function searchItemsForVendors(vendorNbrs, dcNbr) {
  const url = BaseUrl + '/searchItemsForVendors';
  const searchResults = (await Axios.post(url, {
    'vendorNbrs': vendorNbrs,
    'dcNbr': dcNbr
  }, { headers: getHeaders() })).data;
  searchResults.itemInfo = searchResults.itemInfo.map(itemData => new SearchResult(itemData));
  // searchResults.validVendorNbrs = searchResults.validVendorNbrs.map(vendorNbr => vendorNbr.toString());
  return searchResults;
}

async function getItemDetails(itemNbr, legacyItemNbr, dcNbr) {
  const url = BaseUrl + '/fetchItem';
  const res = await Axios.post(url, {
    'itemNbr': itemNbr,
    'legacyItemNbr': legacyItemNbr,
    'dcNbr': dcNbr
  }, { headers: getHeaders() });
  const item = new Item(res.data);
  return item;
}

async function ddirSearch(itemNbr, legacyItemNbr, dcNbr, firstDate) {
  const url = BaseUrl + '/searchDdir';
  const { startDate, endDate } = getStartDateAndEndDateStr(firstDate);
  const res = await Axios.post(url, {
    'itemNbr': itemNbr,
    'legacyItemNbr': legacyItemNbr,
    'dcNbr': dcNbr,
    'startDate': startDate,
    'endDate': endDate
  }, { headers: getHeaders() });
  const weekTable = new WeekTable(res.data, startDate);
  return weekTable;
}

async function getDownloadData(items, dcNbrList) {
  const { startDate, endDate } = getStartDateAndEndDateStrForDownload();
  const url = BaseUrl + '/fetchDataToDownload';

  const res = await Axios.post(url, {
    'itemNbrs': items,
    'dcNbrs': dcNbrList,
    'legacySearch': false,
    'startDate': startDate,
    'endDate': endDate
  }, { headers: getHeaders() });
  let resultData = res.data;
  var returnData = new Map();

  for (let dc in resultData) {
    let dcData = resultData[dc];
    var formattedDcData = new Map();
    for (let itemNbr in dcData) {
      formattedDcData.set(itemNbr, new WeekTable(dcData[itemNbr], startDate));
    }
    returnData.set(dc, formattedDcData);
  }

  return returnData;
}

async function getDownloadDataWithCalculatedValues(items, dcNbrList) {
  const { startDate, endDate } = getStartDateAndEndDateStrForDownload();
  const url = BaseUrl + '/fetchDataToDownloadWithCalculatedValues';

  const res = await Axios.post(url, {
    'itemNbrs': items,
    'dcNbrs': dcNbrList,
    'legacySearch': false,
    'startDate': startDate,
    'endDate': endDate
  }, { headers: getHeaders() });
  let resultData = res.data;
  var returnData = new Map();

  for (let dc in resultData) {
    let dcData = resultData[dc];
    var formattedDcData = new Map();
    for (let itemNbr in dcData) {
      formattedDcData.set(itemNbr, new WeekTable(dcData[itemNbr], startDate));
    }
    returnData.set(dc, formattedDcData);
  }
  return returnData;
}

async function searchWpidItemsInfo(wpids, dcNbr) {
  const url = BaseUrl + '/wpid/getItemsInfo';
  const res = await Axios.post(url, {
    wpids,
    dcNbr: dcNbr ? parseInt(dcNbr, 10) : 0
  }, { headers: getHeaders() });
  const resultMap = res.data;
  const flatResults = [];
  for (const [wpid, items] of Object.entries(resultMap)) {
    for (const item of items) {
      flatResults.push(new WpidSearchResult(item, wpid));
    }
  }
  return flatResults;
}

async function wpidSearchDdir(wpid, dcNbr, firstDate) {
  const url = BaseUrl + '/wpid/searchDdir';
  const { startDate, endDate } = getStartDateAndEndDateStr(firstDate);
  const res = await Axios.post(url, {
    'wpids': wpid,
    'dcNbr': dcNbr,
    'startDate': startDate,
    'endDate': endDate
  }, { headers: getHeaders() });
  const weekTable = new WeekTable(res.data, startDate);
  return weekTable;
}

async function wpidFetchDataToDownload(wpid, dcNbrList) {
  const { startDate, endDate } = getStartDateAndEndDateStrForDownload();
  const url = BaseUrl + '/wpid/fetchDataToDownload';
  const res = await Axios.post(url, {
    'wpids': wpid,
    'dcNbrs': dcNbrList,
    'startDate': startDate,
    'endDate': endDate
  }, { headers: getHeaders() });
  const resultData = res.data;
  const returnData = new Map();
  for (let dc in resultData) {
    returnData.set(dc, new WeekTable(resultData[dc], startDate));
  }
  return returnData;
}

async function wpidSearchDdirTrend(itemNbr, legacyItemNbr, dcNbr) {
  const url = BaseUrl + '/wpid/searchDdirTrend';
  const res = await Axios.post(url, {
    'itemNbr': itemNbr,
    'legacyItemNbr': legacyItemNbr,
    'dcNbr': dcNbr
  }, { headers: getHeaders() });
  const trendTable = new TrendTable(res.data);
  return trendTable;
}

async function searchDdirTrend(itemNbr, legacyItemNbr, dcNbr) {
  const url = BaseUrl + '/searchDdirTrend';
  const res = await Axios.post(url, {
    'itemNbr': itemNbr,
    'legacyItemNbr': legacyItemNbr,
    'dcNbr': dcNbr
  }, { headers: getHeaders() });
  const trendTable = new TrendTable(res.data);
  return trendTable;
}

async function saveComment(itemNbr, legacyItemNbr, dcNbr, commentObj, wpid = null) {
  const url = BaseUrl + '/saveComment';
  const res = await Axios.post(url, {
    'wpid': wpid ?? null,
    'itemNbr': itemNbr,
    'legacyItemNbr': legacyItemNbr,
    'dcNbr': dcNbr,
    'wmtYrWkNbr': commentObj.wmtYrWkNbr,
    'id': commentObj.id,
    'comment': commentObj.commentTxt
  }, { headers: getHeaders() });
  const updatedCommentObj = new DDIRComment(res.data);
  return updatedCommentObj;
}

async function getSavedSearch() {
  const url = BaseUrl + `/getSavedSearch`;
  let savedSearchList = [];
  try {
    savedSearchList = (await Axios.get(url, { headers: getHeaders() }))?.data.map(savedSearchData => new SavedSearch(savedSearchData));
  } catch (e) {
    console.warn('Failed to fetch saved search:', e);
  }
  return savedSearchList;
}

async function addSavedSearch(savedSearchPayload) {
  const url = BaseUrl + '/saveSearch';
  const res = await Axios.post(url, savedSearchPayload, { headers: getHeaders() });
  return res;
}

async function deleteSavedSearch(searchName) {
  const url = BaseUrl + `/deleteSavedSearch/${searchName}`;
  const res = await Axios.delete(url, { headers: getHeaders() });
  return res;
}

export {
  setAuthToken,
  getMetaData,
  getFeatureFlags,
  getDownloadData,
  getDownloadDataWithCalculatedValues,
  searchItems,
  getSavedSearch,
  addSavedSearch,
  deleteSavedSearch,
  getItemDetails,
  ddirSearch,
  searchDdirTrend,
  saveComment,
  searchItemsForVendors,
  searchWpidItemsInfo,
  wpidSearchDdir,
  wpidSearchDdirTrend,
  wpidFetchDataToDownload
};